import React, { useMemo } from 'react';
import { gql } from '@urql/core';
import {
    EventBySlugDocument,
    EventBySlugQuery,
    EventBySlugQueryVariables,
    SsgEventsBySlugDocument,
    EventTicketRegistrationsForUserQuery,
    EventTicketRegistrationsForUserQueryVariables,
    EventTicketRegistrationsForUserDocument,
    Espin_Role_Enum,
} from '../../../generated/spin-graphql';
import { PathsForPage, UrqlSSGForPage } from '../../../server/utils/serverUtils/ssg.utils';
import { GetStaticProps } from 'next';
import { Layout } from '../../../components/common/layout/Layout';
import { EventHeader } from '../../../components/events/event-page/EventHeader';
import { EventInfo } from '../../../components/events/event-page/EventInfo';
import { EventRegistration } from '../../../components/events/event-page/EventRegistration';
import { useQuery } from 'urql';
import { CustomSEO } from '../../../components/common/layout/HeadMeta';
import { GenericFullPageLoading } from '../../../components/common/GenericFullPageLoading';
import { useAuthenticatedQuery } from '../../../client/hooks';
import { useAppState } from '../../../client/providers/AppProvider';

gql`
    query EventBySlug($eventSlug: String!) {
        espin_event(where: { slug: { _eq: $eventSlug }, status: { _eq: published } }) {
            id
            ...EventHeader
            ...EventInfo
            ...EventRegistration
            name
            short_description
            slug
            event_start_date
            event_end_date
            url
            register_start_date
            register_end_date
            stream_url
            body_content
            event_tickets {
                ...EventTicketCard
            }
            logo {
                id
                ext
                cdn_url
            }
            icon {
                id
                ext
                cdn_url
            }
            type
            event_games {
                id
                game_id
                game {
                    game_definition
                    name
                    website
                    image_url
                    team_max_size
                }
            }
        }
    }

    query EventTicketRegistrationsForUser($eventSlug: String!) {
        espin_event(where: { slug: { _eq: $eventSlug }, status: { _eq: published } }) {
            ...TicketRegistrations
        }
    }
`;

export default function EventBySlug({ variables }: { variables: EventBySlugQueryVariables }) {
    const { user } = useAppState();
    const [result] = useQuery<EventBySlugQuery>({
        query: EventBySlugDocument,
        variables,
        context: useMemo(() => ({ additionalTypenames: ['espin_event'] }), []),
    });
    const { espin_event } = result.data ?? {};

    const { data: ticketRegistrations } = useAuthenticatedQuery<
        EventTicketRegistrationsForUserQuery,
        EventTicketRegistrationsForUserQueryVariables
    >(
        {
            query: EventTicketRegistrationsForUserDocument,
            variables,
            pause: !user || user?.role === Espin_Role_Enum.EspinCollegeUser,
        },
        ['espin_event_registration', 'espin_event'],
    );

    // add a catch in case it is still fetching
    // this normally should not be needed, but we're seeing some odditied
    if (result.fetching) {
        return <GenericFullPageLoading />;
    }
    const [eventData] = espin_event;

    const seoOverride: CustomSEO = {
        title: eventData.name,
        description: eventData.short_description,
    };

    return (
        <Layout seoOverride={seoOverride}>
            <div className="event-page">
                <EventHeader eventData={eventData} />
                <div className="container event-page-container">
                    <EventInfo eventData={eventData} />
                    <EventRegistration
                        eventData={eventData}
                        ticketRegistrations={ticketRegistrations?.espin_event[0]}
                    />
                    {/* {eventData.sponsors && ( */}
                    {/* <EventSponsorsCard
                        className="mobile-sponsors-card"
                        sponsors={defaultSponsors}
                        logoPosition={'center'}
                    /> */}
                    {/* )} */}
                </div>
            </div>
        </Layout>
    );
}

gql`
    query SSGEventsBySlug {
        espin_event(where: { _and: [{ status: { _eq: published } }, { slug: { _is_null: false } }] }) {
            id
            slug
        }
    }
`;
export async function getStaticPaths() {
    return PathsForPage(SsgEventsBySlugDocument, 'espin_event', 'slug', 'eventSlug');
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    return UrqlSSGForPage<EventBySlugQuery>(EventBySlugDocument, ctx.params);
};
